import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Users, Building, Globe, ArrowRight } from 'lucide-react';

const regionalOffices = [
  { slug: 'west-africa', name: 'OPAD West Africa', hq: 'Lagos, Nigeria', countries: 6, focus: 'Poverty alleviation, education & humanitarian response', color: '#E08B7A' },
  { slug: 'east-africa', name: 'OPAD East Africa', hq: 'Addis Ababa, Ethiopia', countries: 7, focus: 'Livelihoods, climate resilience & gender equity', color: '#4A7C59' },
  { slug: 'southern-africa', name: 'OPAD Southern Africa', hq: 'Johannesburg, South Africa', countries: 5, focus: 'Sustainable development & community empowerment', color: '#E5B75F' },
  { slug: 'europe', name: 'OPAD Europe', hq: 'Brussels, Belgium', countries: 4, focus: 'Policy engagement, partnerships & EU cooperation', color: '#003399' },
  { slug: 'usa', name: 'OPAD USA', hq: 'New York, USA', countries: 1, focus: 'UN engagement, youth programmes & advocacy', color: '#7BC4E2' },
  { slug: 'asia', name: 'OPAD Asia', hq: 'Beijing, China', countries: 3, focus: 'SDG internships, education & partnerships', color: '#9DB9A3' },
];

const regionsSummary = [
  { name: 'Africa', countries: 18, offices: 3, description: 'Our largest operational region — East, West and Southern Africa.' },
  { name: 'Europe', countries: 4, offices: 1, description: 'EU institutional engagement and strategic partnerships.' },
  { name: 'Americas', countries: 1, offices: 1, description: 'UN engagement and advocacy from our New York chapter.' },
  { name: 'Asia', countries: 3, offices: 1, description: 'SDGs internship programme and regional partnerships.' },
];

export default function WhereWeWorkPage() {
  return (
    <div className="min-h-screen bg-[#FDF8F4] pt-20" data-testid="where-we-work-page">
      {/* Hero */}
      <section className="relative section-padding overflow-hidden bg-gradient-to-br from-[#4A7C59] via-[#3D6249] to-[#2A4B33] text-white">
        <div className="absolute top-0 right-0 w-[420px] h-[360px] bg-[#E5B75F]/15 rounded-bl-[200px]" />
        <div className="container-custom relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2 mb-5">
            <Globe className="w-5 h-5 text-[#E5B75F]" />
            <span className="text-sm font-medium">Global Footprint</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] mb-5">Where We Work</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-3xl mx-auto">
            OPAD operates six regional offices across Africa, Europe, the Americas and Asia — delivering community-driven programmes rooted in local expertise.
          </p>
        </div>
      </section>

      {/* Region summary */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center max-w-4xl mx-auto">
            {regionsSummary.map((r) => (
              <div key={r.name}>
                <div className="text-3xl md:text-4xl font-extrabold text-[#4A7C59]">{r.countries}</div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Countries · {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Offices Grid */}
      <section className="section-padding" data-testid="regional-offices-grid">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-3">Our Regional Offices</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Each regional office translates OPAD's global mission into locally relevant, community-driven programmes.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {regionalOffices.map((office, idx) => (
              <motion.div
                key={office.slug}
                className="bg-white rounded-3xl overflow-hidden shadow-md border border-slate-100 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`office-card-${office.slug}`}
              >
                <div className="h-2" style={{ backgroundColor: office.color }} />
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-5 h-5" style={{ color: office.color }} />
                    <h3 className="font-bold text-lg text-slate-900">{office.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{office.hq}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                    <Users className="w-4 h-4" />
                    <span>{office.countries} countries served</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{office.focus}</p>
                  <Link
                    to={`/opad-${office.slug}`}
                    className="mt-auto inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition"
                    style={{ color: office.color }}
                  >
                    Explore office <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#003399] to-[#1B4DA0] text-white">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] mb-4">Want to work with a specific region?</h2>
          <p className="text-white/85 mb-8">Contact the regional office directly or explore partnerships with our global team.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#E5B75F] text-slate-900 hover:bg-[#D4A64E] rounded-full px-8 py-3 font-semibold transition">
              Get in Touch <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/get-involved" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#003399] rounded-full px-8 py-3 font-semibold transition">
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
